import { GameReview, retrieveAllGameReviews, retrieveGameReviewBySlug } from "@/utils/api/prismic/prismicClient";
import { embedSerializer } from "@/utils/reviewEmbedSerializer";
import { PrismicRichText } from "@prismicio/react";
import RiArrowLeftLine from '~icons/ri/arrow-left-line'
import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import Image from "next/image";
import Link from "next/link";
import classNames from "classnames";
import { HeadMetadata } from "@/components/HeadMetadata";
import { datetimeInLocaleLanguageAndTimezone } from "@/utils/timeFormatters";
import RootLayout from "../RootLayout";
import { ReactElement } from "react";
import { NextPageWithLayout } from "../_app";

interface Props {
  gameReview: GameReview;
  siteUrl: string;
}

const GameReviewPage: NextPageWithLayout<Props> = (props) => {
  const serializer = embedSerializer;
  const { cover } = props.gameReview;
    
  return (
    <>
      <HeadMetadata
        pageTitle={`${props.gameReview.title} › Игровые рецензии › Тиджой`}
        pageDescription={`Обзор на игру ${props.gameReview.title} от Тиджоя, а также ссылки на плейлисты и видео.`}
        canonicalUrl={`${props.siteUrl}/game-reviews/${props.gameReview.slug}`}
        robots="index,follow"
        image={
          cover === null
            ? undefined
            : {
                alt: cover.alt,
                dimensions: cover.sizes.original.dimensions,
                url: cover.sizes.original.url,
              }
        }
      />

      <article className='max-w-2xl mx-auto flex flex-col gap-5 rounded-xl order-first md:order-none md:pr-5'>
        <Image
          src={props.gameReview!.cover!.sizes!.preview.url}
          alt={props.gameReview!.cover!.alt}
          width={640}
          height={320}
          className='w-full rounded-xl'/>

        <section className='flex flex-row flex-wrap gap-3 items-center'>
          <p className={'bg-[#0B0B0B] px-3 py-1 rounded-xl text-base'}>{props.gameReview.score.label} {props.gameReview.score.emoji}</p>
          <h1 className={'text-2xl font-bold'}>{props.gameReview.title}</h1>
        </section>

        <section className='flex flex-col gap-1'>
          <p className='text-zinc-500'>Описание</p>
          <section className='flex flex-col gap-5'>
            <PrismicRichText
              field={props.gameReview.review}
              components={serializer}/>
          </section>
        </section>

        <section className='flex flex-row flex-wrap gap-2 justify-start'>
          <div className='inline-flex flex-row md:flex-row flex-wrap gap-2 w-full'>
            <p className='bg-[#0B0B0B] py-3 px-5 rounded-xl text-center grow'>
                <span className='text-zinc-500 mr-1'>Дата создания</span> {datetimeInLocaleLanguageAndTimezone(new Date(props.gameReview.createdAt))}
            </p>
            {props.gameReview.genres.map((genre, index) =>
              <p key={index} className={classNames('bg-[#0B0B0B] py-3 px-5 rounded-xl text-center grow md:grow-0', {'col-span-2': genre.length >= 12})}>{genre}</p>
            )}
          </div>
        </section>
            
        <Link
          href="/game-reviews"
          className="flex flex-row items-center justify-center mx-auto text-xs md:text-base gap-3 hover:bg-[#282828]/50
          bg-[#151515]/50 rounded-2xl px-5 p-2 md:px-8 border hover:border-white/30 border-white/10 w-fit mt-3 md:mt-5 transition-all duration-200 ease-in-out">
            <RiArrowLeftLine/> вернуться назад 
        </Link>
      </article>
    </>
  )
}

GameReviewPage.getLayout = function getLayout(page: ReactElement) {
  return (
      <RootLayout>{page}</RootLayout>
  )
}  

export default GameReviewPage;

type QueryParams = {
  slug: string;
};

export const getStaticProps: GetStaticProps<Props, QueryParams> = async (context) => {
  if (context.params === undefined) {
    return {
      notFound: true,
    };
  }

  const gameReview = await retrieveGameReviewBySlug(
    process.env['PRISMIC_REPOSITORY'] as string,
    process.env['PRISMIC_ACCESS_TOKEN'] as string,
    context.params.slug,
  );

  if (gameReview === undefined) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      gameReview,
      siteUrl: process.env['SITE_URL'] as string,
    },
  };
};


export const getStaticPaths: GetStaticPaths = async () => {
  const gameReviews = await retrieveAllGameReviews(
      process.env['PRISMIC_REPOSITORY'] as string, 
      process.env['PRISMIC_ACCESS_TOKEN'] as string
  );

  return {
      fallback: false,
      paths: gameReviews.map((gameReview) => ({
          params: {
              slug: gameReview.slug,
          },
      })),
  };
};